import {
  ClinicalDataToShare,
  ClinicalItem,
  ClinicalItemLog,
  WithPerfusionTime,
} from '@features/components/ClinicalData/types';
import {
  ClinicalDataAttachmentMedia,
  ClinicalDataShareMessage,
  LabeledEntries,
} from '@features/components/Chat/components/ChatContent/Messages/types/chatMessages';
import { liverAnatomyDescription, wolsNames } from '@features/components/ClinicalData/constants';
import { createChatMediaFilesDTO } from '@features/components/Chat/components/ChatContent/Messages/utils/common';
import { isNil } from 'lodash-es';
import { heartPassOptionLabels } from '@features/components/ClinicalData/components/ClinicalLog/constants';
import { VitalsFormatted } from '@features/components/ClinicalData/sections/utils';
import {
  getVitalsRT,
  getVitalsWithNamesParameters,
} from '@features/components/ClinicalData/components/ClinicalLog/components/vitals/utils';
import {
  getBloodSampleData,
  getBloodSampleRadiometerName,
  getNRPType,
  getPerfusionTerminationType,
  getPfRatioInSituData,
  getPfRatioOnOCSData,
} from '@features/components/ClinicalData/components/ClinicalLog/LogItems/utils';
import {
  ClinicalDataNameValue,
  WithCommonMeasurements,
} from '@features/components/ClinicalData/components/ClinicalLog/LogItems/types';
import {
  dispositionReasonsNames,
  dispositionSpecificationNames,
  randomizationTypeNames,
  turndownOutcomeNames,
} from '@/constants/case/case.constants';
import { convertToIanaTimezone, formatDate, getNewLine, joinWithNewline, toStringValue, yesNo } from '@/utils/helpers';
import { ClinicalItemType, HeartPassOptionType, RadiometerType, WithdrawalOfLifeSupportData } from '@/api/apis/case';
import { getOrganApprovalMessage, getOrganDeclineMessage } from '../messageFormatters';

export const wrapValueWithBreak = (itemValue: string) => {
  return `${getNewLine()}${itemValue}${getNewLine()}`;
};

const addBreakToLastItem = (itemValue: string, arrLength: number, index: number) => {
  if (arrLength - 1 === index) {
    return `${itemValue}${getNewLine()}`;
  }

  return itemValue;
};

export const formatSharedDataTimestamp = (timestamp: string, timeZone: string): string => {
  return formatDate(timestamp, 'MM/dd/yy HH:mm zzz', { timezone: convertToIanaTimezone(timeZone) });
};

const mapClinicalObjectToEntries = (data: Record<string, string | number>): LabeledEntries[] => {
  return Object.entries(data).map(([title, val]) => ({
    title,
    value: String(val),
  }));
};

const mapClinicalDataWithCommonMeasurementsToEntries = (
  clinicalDataObj: WithCommonMeasurements<object | ClinicalDataNameValue[]> | null,
): LabeledEntries[] => {
  const entries: LabeledEntries[] = [];
  const { commonMeasurements, data } = clinicalDataObj ?? {};

  if (!commonMeasurements || !data) {
    return [];
  }

  if (commonMeasurements.length) {
    commonMeasurements.forEach((item: ClinicalDataNameValue) => {
      entries.push(...mapClinicalObjectToEntries({ [item.name]: item.value }));
    });
  }

  if (Array.isArray(data) && data.length) {
    data.forEach((item: ClinicalDataNameValue) => {
      entries.push(...mapClinicalObjectToEntries({ [item.name]: item.value }));
    });
  } else {
    Object.entries(data).forEach(([, entryValue]: [entryKey: string, entryValue: ClinicalDataNameValue[]]) => {
      if (Array.isArray(entryValue)) {
        entryValue.forEach((item: ClinicalDataNameValue, index: number) => {
          entries.push(
            ...mapClinicalObjectToEntries({ [item.name]: addBreakToLastItem(item.value, entryValue.length, index) }),
          );
        });
      }
    });
  }

  return entries;
};

export const generateDryRunSubtitleDetails = ({
  dispositionReason,
  dispositionSpecification,
  turndownOutcome,
}: ClinicalItemLog<
  ClinicalItemType.DryRun | ClinicalItemType.Turndown | ClinicalItemType.OneLungTurndown
>): string[] => {
  return [
    `Cancellation reason: ${dispositionReason ? dispositionReasonsNames[dispositionReason] : 'N/A'}`,
    `Specification: ${dispositionSpecification ? dispositionSpecificationNames[dispositionSpecification] : 'N/A'}`,
    `Outcome: ${turndownOutcome ? turndownOutcomeNames[turndownOutcome] : 'N/A'}`,
  ];
};

export const generateVitalsEntries = (vitals: VitalsFormatted): LabeledEntries[] => {
  return getVitalsWithNamesParameters(vitals.vitalParameters).map(({ name: title, value }) => ({ title, value }));
};

export const getClinicalDataExtras = (
  record: WithPerfusionTime<ClinicalItemLog>,
  isTrialCase: boolean,
  activeReading?: ClinicalDataToShare,
): Omit<ClinicalDataShareMessage, 'title' | 'media'> => {
  const { clinicalItemType, perfusionTime, yesNo: yesNoRaw, details } = record;
  const { text } = record as WithdrawalOfLifeSupportData;
  const formattedTimeTimestampValue = formatSharedDataTimestamp(record.timestamp, record.timeZone);
  const perfusionTimeValue = perfusionTime ? `PT ${perfusionTime}` : '';
  const yesNoValue = isNil(yesNoRaw) ? '' : yesNo(yesNoRaw);
  const detailsValue = joinWithNewline(text ?? '', details ?? '');
  const clinicalDataDefault: string[] = [formattedTimeTimestampValue, perfusionTimeValue, detailsValue];

  switch (clinicalItemType) {
    case ClinicalItemType.NRPStart:
    case ClinicalItemType.DonorInOR:
    case ClinicalItemType.Heparin:
    case ClinicalItemType.SkinIncisionTime:
    case ClinicalItemType.CrossClampTimeFlushTime:
    case ClinicalItemType.FinalFlush:
    case ClinicalItemType.OCSDeviceDeployment:
    case ClinicalItemType.DeployedToDonorHospital:
    case ClinicalItemType.AtDonorHospital:
    case ClinicalItemType.OrganOnOCS:
    case ClinicalItemType.LeavingDonorHospital:
    case ClinicalItemType.ArrivalAtRecipientHospital:
    case ClinicalItemType.LeavingRecipientHospital:
    case ClinicalItemType.SpecialistIsDone:
    case ClinicalItemType.PFOValveCondition:
    case ClinicalItemType.OffOCS:
    case ClinicalItemType.Priming:
    case ClinicalItemType.CardiacArrest:
    case ClinicalItemType.DonorDidNotExpireInTime:
    case ClinicalItemType.PreOCSFlush:
    case ClinicalItemType.WithdrawalOfLifeSupportTime:
    case ClinicalItemType.RecordDelay:
    case ClinicalItemType.DeathDeclarationTime:
    case ClinicalItemType.ScheduledDonorORStartTime:
    case ClinicalItemType.OrganAccepted:
      return {
        subtitle: joinWithNewline(...clinicalDataDefault),
        labeledEntries: [],
      };
    case ClinicalItemType.BloodAvailabilityCheck:
    case ClinicalItemType.DonorLabelsCheck:
    case ClinicalItemType.BronchCartAndSuctionCheck:
    case ClinicalItemType.ReintubationPersonalCheck:
    case ClinicalItemType.OnSiteCoordinatorsContacts:
    case ClinicalItemType.PickUpOfOrganLabelAndTissueSamples:
    case ClinicalItemType.IceForTheOCSCoolerPriorDeparture:
    case ClinicalItemType.CysticDuct:
    case ClinicalItemType.ResearchBloodSamples:
    case ClinicalItemType.TeamHuddle:
    case ClinicalItemType.Bile:
    case ClinicalItemType.TxOCSBiopsy:
    case ClinicalItemType.DonorBiopsyReport:
    case ClinicalItemType.LiverOnABackBench:
      return {
        subtitle: joinWithNewline(formattedTimeTimestampValue, perfusionTimeValue, yesNoValue, detailsValue),
        labeledEntries: [],
      };
    case ClinicalItemType.LiverAnatomyQuestionnaire:
      return {
        subtitle: joinWithNewline(
          formattedTimeTimestampValue,
          perfusionTimeValue,
          `Value: ${liverAnatomyDescription[record.answer]}`,
          detailsValue,
        ),
        labeledEntries: [],
      };
    case ClinicalItemType.LiverReconstructionQuestionnaire:
      return {
        subtitle: joinWithNewline(
          formattedTimeTimestampValue,
          perfusionTimeValue,
          yesNoValue,
          record.answer ?? '',
          detailsValue,
        ),
        labeledEntries: [],
      };
    case ClinicalItemType.WithdrawalOfLifeSupport:
      return {
        subtitle: joinWithNewline(
          formattedTimeTimestampValue,
          perfusionTimeValue,
          wolsNames[record.answer],
          detailsValue,
        ),
        labeledEntries: [],
      };
    case ClinicalItemType.BloodCollection:
      return {
        subtitle: joinWithNewline(
          formattedTimeTimestampValue,
          perfusionTimeValue,
          `Volume: ${record.volume}ml`,
          detailsValue,
        ),
        labeledEntries: [],
      };
    case ClinicalItemType.HeartPassOption:
      return {
        subtitle: joinWithNewline(
          formattedTimeTimestampValue,
          perfusionTimeValue,
          heartPassOptionLabels[record.heartPassOptionType as HeartPassOptionType],
          detailsValue,
        ),
        labeledEntries: [],
      };
    case ClinicalItemType.DryRun:
      return {
        subtitle: joinWithNewline(
          formattedTimeTimestampValue,
          perfusionTimeValue,
          ...generateDryRunSubtitleDetails(record),
          detailsValue,
        ),
        labeledEntries: [],
      };
    case ClinicalItemType.CheckInOCSDevice: // not visible
      // if used, should be implemented
      /* "System is in standby: \(value.systemIsInStandby ? "Yes" : "No")",
      "Monitor is Docked: \(value.monitorIsDocked ? "Yes" : "No")",
      "Cleaning and desinfection complete: \(value.cleaningAndDisinfectionComplete ? "Yes" : "No")",
      "System is AC powered: \(value.systemIsACPowered ? "Yes" : "No")" */
      return {
        subtitle: joinWithNewline(...clinicalDataDefault),
        labeledEntries: [],
      };
    case ClinicalItemType.SupplementalOrgan: // not visible
      // if used, should be implemented
      /* let timestamp = value.items.first?.timestamp ?? Date()
      let timezone = value.items.first?.timeZone ?? .currentTimezone()
      let perfusionDuration = clinicalCase.perfusionDuration(for: timestamp)
      let kidneyOption = value.items.first { $0.organ == .kidney }?.yesNo ?? .no
      let pancreasOption = value.items.first { $0.organ == .pancreas }?.yesNo ?? .no
      subtitle = [
        "\(timestamp.formatMMDDYYYHHMM) \(timezone.abbreviation(timestamp))",
        perfusionDuration != nil ? "PT \(perfusionDuration!.formatHHMM)" : "",
        "Kidney: \(kidneyOption.rawValue)",
        "Pancreas: \(pancreasOption.rawValue)"
      ] */
      return {
        subtitle: joinWithNewline(...clinicalDataDefault),
        labeledEntries: [],
      };
    case ClinicalItemType.NRPApplicabilityAndType:
      return {
        subtitle: joinWithNewline(
          formattedTimeTimestampValue,
          perfusionTimeValue,
          yesNoValue,
          getNRPType(record.nrpType),
        ),
        labeledEntries: [],
      };
    case ClinicalItemType.BronchVideoOnTheOCS:
    case ClinicalItemType.FinalBronchVideoAtTheRecipientHospital:
    case ClinicalItemType.OnOCSVideo:
    case ClinicalItemType.Stabilization:
    case ClinicalItemType.PostFlushLungsPhoto:
    case ClinicalItemType.VisualInspection:
    case ClinicalItemType.FinalPhysicalExamPictureOrVideo:
    case ClinicalItemType.InSituVideoOfHeart:
    case ClinicalItemType.OffOCSPhotoAndVideo:
    case ClinicalItemType.OrganMonitoring:
    case ClinicalItemType.AnatomyPhotos:
    case ClinicalItemType.PostFlushBronch:
    case ClinicalItemType.FinalCheck:
    case ClinicalItemType.BronchInSitu:
    case ClinicalItemType.ReconstructionVideosPhotos:
      return {
        subtitle: joinWithNewline(formattedTimeTimestampValue, perfusionTimeValue),
        labeledEntries: [],
      };
    case ClinicalItemType.PFRatioInSitu:
      return {
        subtitle: joinWithNewline(formattedTimeTimestampValue, perfusionTimeValue),
        labeledEntries: mapClinicalDataWithCommonMeasurementsToEntries(getPfRatioInSituData(record, isTrialCase)),
      };
    case ClinicalItemType.PFRatioOnOCSDonorHospital:
    case ClinicalItemType.PFRatioOnOCSTXCenter:
      return {
        subtitle: joinWithNewline(formattedTimeTimestampValue, perfusionTimeValue),
        labeledEntries: mapClinicalDataWithCommonMeasurementsToEntries(getPfRatioOnOCSData(record, isTrialCase)),
      };
    case ClinicalItemType.BloodSample:
    case ClinicalItemType.PreOCSBloodSample:
      return {
        subtitle: wrapValueWithBreak(
          joinWithNewline(
            formattedTimeTimestampValue,
            perfusionTimeValue,
            getBloodSampleRadiometerName(record.radiometerType as RadiometerType),
          ),
        ),
        labeledEntries: [
          ...mapClinicalDataWithCommonMeasurementsToEntries(getBloodSampleData(record.radiometer)),
          ...(details ? [{ title: 'Details', value: details }] : []),
        ],
      };
    case ClinicalItemType.DonorSpecimenHandoff:
      return {
        subtitle: joinWithNewline(
          formattedTimeTimestampValue,
          perfusionTimeValue,
          `Name: ${toStringValue(record.signature?.fullName)}`,
          `Credentials: ${toStringValue(record.signature?.credentials)}`,
          detailsValue,
        ),
        labeledEntries: [],
      };
    case ClinicalItemType.VitalUpdates:
      return {
        subtitle: getVitalsRT((activeReading as VitalsFormatted).measurementMinute),
        labeledEntries: generateVitalsEntries(activeReading as VitalsFormatted),
      };
    case ClinicalItemType.ScsTerminationOption:
      return {
        subtitle: joinWithNewline(formattedTimeTimestampValue, perfusionTimeValue),
        labeledEntries: [],
      };
    case ClinicalItemType.PerfusionTerminationOption:
      return {
        subtitle: joinWithNewline(
          `Type: ${getPerfusionTerminationType(record.details) ?? 'N/A'}`,
          formattedTimeTimestampValue,
          perfusionTimeValue,
        ),
        labeledEntries: [],
      };
    case ClinicalItemType.Randomization:
      return {
        subtitle: joinWithNewline(
          record.result ? randomizationTypeNames[record.result] : '',
          formattedTimeTimestampValue,
          perfusionTimeValue,
        ),
        labeledEntries: [],
      };
    case ClinicalItemType.OrganApproved:
      return {
        subtitle: joinWithNewline(formattedTimeTimestampValue, perfusionTimeValue, getOrganApprovalMessage(record)),
        labeledEntries: [],
      };
    case ClinicalItemType.OrganDeclined:
      return {
        subtitle: joinWithNewline(formattedTimeTimestampValue, perfusionTimeValue, getOrganDeclineMessage(record)),
        labeledEntries: [],
      };

    default:
      return {
        subtitle: joinWithNewline(...clinicalDataDefault),
        labeledEntries: [],
      };
  }
};

const getMedia = (
  record: WithPerfusionTime<ClinicalItemLog | ClinicalItem>,
  activeReading?: ClinicalDataToShare,
): ClinicalDataAttachmentMedia[] => {
  if (activeReading?.file) {
    return createChatMediaFilesDTO([activeReading.file]);
  }

  if (record.mediaFiles?.length) {
    return createChatMediaFilesDTO(record.mediaFiles);
  }
  return [];
};

export const getClinicalDataShareMessage = ({
  record,
  activeReading,
  clinicalLogAppNames,
  isTrialCase,
}: {
  record: WithPerfusionTime<ClinicalItemLog | ClinicalItem>;
  activeReading?: ClinicalDataToShare;
  clinicalLogAppNames: Record<ClinicalItemType, string>;
  isTrialCase: boolean;
}): ClinicalDataShareMessage => {
  return {
    title: clinicalLogAppNames[record.clinicalItemType],
    ...getClinicalDataExtras(record as WithPerfusionTime<ClinicalItemLog>, isTrialCase, activeReading),
    media: getMedia(record, activeReading),
  };
};
